// GRAVY-10073 / GRAVY-10101: shared helper for the staffing wizards'
// "Hours This Week" calculation. Returns the boundaries of the payroll
// workweek (US/Eastern, Monday 04:00 → next Monday 03:59:59) so the
// frontend's per-row at-risk highlighting agrees with the backend's
// `Workers::Overtime::WeeklyHoursService` / `ProjectionService` bucketing
// (which anchor on the same Eastern Monday-04:00 boundary via
// `TimeRange.payroll_week_start_*`). Bucketing in UTC instead under-counted
// the local week on EDT Sunday evenings (a Sun 8 PM ET shift fell into the
// next UTC week), so the over-40 warning failed to fire.

export type WeekRange = { startMs: number; endMs: number };

const TZ = 'America/New_York';
const FOUR_HOURS_MS = 4 * 3_600_000;

const EASTERN_DTF = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

type WallParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

// Eastern wall-clock components for a given instant.
const easternParts = (instantMs: number): WallParts => {
  const map: Record<string, string> = {};
  for (const p of EASTERN_DTF.formatToParts(new Date(instantMs))) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  let hour = parseInt(map.hour, 10);
  if (hour === 24) hour = 0; // some engines emit "24" for midnight
  return {
    year: +map.year,
    month: +map.month,
    day: +map.day,
    hour,
    minute: +map.minute,
    second: +map.second,
  };
};

// How far ahead of UTC the Eastern zone is at this instant, in ms
// (negative: EDT = −4h, EST = −5h).
const easternOffsetMs = (instantMs: number): number => {
  const p = easternParts(instantMs);
  const wallAsUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return wallAsUtc - instantMs;
};

// Convert an Eastern wall-clock time to the matching UTC instant. Uses the
// single-correction technique (with a second pass for the DST transition
// edge): guess the offset by treating the wall time as UTC, then refine.
const easternWallToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute = 0,
  second = 0,
): number => {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset1 = easternOffsetMs(asUtc);
  let utcMs = asUtc - offset1;
  const offset2 = easternOffsetMs(utcMs);
  if (offset2 !== offset1) utcMs = asUtc - offset2;
  return utcMs;
};

export const currentWeekRangeEastern = (now: Date = new Date()): WeekRange => {
  const p = easternParts(now.getTime());
  // Treat the Eastern wall clock as a UTC-encoded value for pure calendar
  // math, shift back 4h so the payroll day rolls over at 04:00 Eastern,
  // then snap to that week's Monday.
  const wallAsUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  const shifted = new Date(wallAsUtc - FOUR_HOURS_MS);
  const daysFromMonday = (shifted.getUTCDay() + 6) % 7;
  const monday = new Date(
    Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate() - daysFromMonday,
    ),
  );
  const startMs = easternWallToUtcMs(
    monday.getUTCFullYear(),
    monday.getUTCMonth() + 1,
    monday.getUTCDate(),
    4,
  );
  const nextMonday = new Date(
    Date.UTC(
      monday.getUTCFullYear(),
      monday.getUTCMonth(),
      monday.getUTCDate() + 7,
    ),
  );
  const endMs = easternWallToUtcMs(
    nextMonday.getUTCFullYear(),
    nextMonday.getUTCMonth() + 1,
    nextMonday.getUTCDate(),
    4,
  );
  return { startMs, endMs };
};

export const isInCurrentWeekEastern = (
  startAtIso: string,
  range: WeekRange = currentWeekRangeEastern(),
): boolean => {
  const ms = new Date(startAtIso).getTime();
  if (!Number.isFinite(ms)) return false;
  return ms >= range.startMs && ms < range.endMs;
};

// Sums shift duration (hours) for the subset of `shifts` whose start_at
// falls within the current payroll workweek (Eastern, Monday 04:00). Used
// by the staffing wizards to feed `HoursThisWeekCell.selectedShiftHours`
// with a week-aware value (rather than the cumulative total across all
// selected weeks).
//
// GRAVY-10073 break-aware: each shift may carry `mandatory_break_minutes`
// — the helper subtracts the value from gross so the per-row at-risk red
// matches the backend's net-of-break projection. Missing/0 break preserves
// the legacy gross behavior. `Math.max(..., 0)` clamps the data-corruption
// case where break > gross.
export const sumCurrentWeekShiftHours = (
  shifts: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>,
  now: Date = new Date(),
): number => {
  const range = currentWeekRangeEastern(now);
  let total = 0;
  for (const s of shifts) {
    if (!isInCurrentWeekEastern(s.start_at, range)) continue;
    const startMs = new Date(s.start_at).getTime();
    const endMs = new Date(s.end_at).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    const breakHours = (s.mandatory_break_minutes ?? 0) / 60;
    const netHours = (endMs - startMs) / 3_600_000 - breakHours;
    total += Math.max(netHours, 0);
  }
  return Number(total.toFixed(2));
};
